import { lazy, Suspense, useContext, useEffect, type ReactNode } from 'react'
import { BrowserRouter, Navigate, Outlet, Route, Routes, useLocation, useOutletContext, useSearchParams } from 'react-router-dom'
import { AuthContext, AuthProvider, isPlatformUser, type AdminUser } from '@retink/web-auth'
import { NotFoundState, Toaster, toast } from '@retink/web-ui'
import { api } from './lib/api'
import { AppShell } from './shell/AppShell'
import { TenantDashboard } from './pages/Dashboard'
import { Settings } from './pages/Settings'
import { Conversations, ConversationDetail } from './pages/Conversations'
import VoiceSettings from './pages/VoiceSettings'
import ChannelRoutes from './pages/ChannelRoutes'
import Queues from './pages/Queues'
import { ChooseTenant } from './pages/ChooseTenant'
import { ModuleBoundary, ModuleReady } from './components/ModuleBoundary'
import type { TenantContext } from './lib/tenant-context'

const AdminModule = lazy(() => import('admin'))
const FilesystemModule = lazy(() => import('@filesystem/web'))
const SqldbModule = lazy(() => import('@sqldb/web'))
const KnowledgeModule = lazy(() => import('@knowledge/web'))
const FlowsModule = lazy(() => import('@flows/web'))
const AgentsModule = lazy(() => import('@agents/web'))

const ID_URL = import.meta.env['VITE_ID_URL'] ?? 'https://app.retink.test:9010/signin'

export function App() {
  return (
    <AuthProvider idUrl={ID_URL} fetchMe={() => api.get<{
      user: AdminUser | null
      bootstrap?: TenantContext | null
    }>('/api/auth/me')}>
      <Toaster />
      <BrowserRouter basename={import.meta.env.BASE_URL}>
        <SsoFeedback />
        <Routes>
          <Route path="/choose-tenant" element={<ChooseTenant />} />
          <Route path="/manage/*" element={<LegacyManageRedirect />} />

          <Route element={<AppShell />}>
            <Route index element={<HomeRedirect />} />
            <Route path="account" element={<Settings />} />
            <Route path="admin/*" element={<LazyModule id="admin" name="Administration"><AdminModule /></LazyModule>} />
            <Route path="orgs" element={<Navigate to="/admin/organizations" replace />} />
            <Route path="organizations" element={<Navigate to="/admin/organizations" replace />} />

            <Route element={<TenantMemberOnly />}>
              <Route path="dashboard" element={<TenantDashboard />} />
              <Route path="conversations" element={<Conversations />} />
              <Route path="conversations/:id" element={<ConversationDetail />} />
              <Route path="channels" element={<Navigate to="/channels/routes" replace />} />
              <Route path="channels/routes" element={<ChannelRoutes />} />
              <Route path="channels/queues" element={<Queues />} />
              <Route path="channels/voice" element={<VoiceSettings />} />
              <Route path="filesystem/*" element={<LazyModule id="filesystem" name="Files"><FilesystemModule /></LazyModule>} />
              <Route path="sqldb/*" element={<LazyModule id="sqldb" name="Database"><SqldbModule /></LazyModule>} />
              <Route path="knowledge/*" element={<LazyModule id="knowledge" name="Knowledge"><KnowledgeModule /></LazyModule>} />
              <Route path="flows/*" element={<LazyModule id="flows" name="Flows"><FlowsModule /></LazyModule>} />
              <Route path="agents/*" element={<LazyModule id="agents" name="AI Agents"><AgentsModule /></LazyModule>} />
            </Route>

            <Route path="*" element={<NotFoundState message="That application route doesn’t exist." />} />
          </Route>
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  )
}

const SSO_ERRORS: Record<string, string> = {
  access_denied: 'Microsoft sign-in was cancelled or denied.',
  consent_not_granted: 'Microsoft admin consent was not granted.',
  directory_mismatch: 'The Microsoft account belongs to a different directory.',
  organization_not_registered: 'That Microsoft organization is not registered with Retink.',
  no_account: 'That Microsoft account does not have access to Retink.',
  invite_invalid: 'This owner setup link is invalid or has expired.',
  invalid_state: 'The sign-in session expired. Please try again.',
  token_verification_failed: 'Microsoft sign-in could not be verified. Try again or contact platform support.',
  account_inactive: 'This account is inactive.',
}

function SsoFeedback() {
  const [params, setParams] = useSearchParams()
  const error = params.get('sso_error')
  const consent = params.get('sso_consent')
  const activated = params.get('sso')

  useEffect(() => {
    if (error) toast.error(SSO_ERRORS[error] ?? `Microsoft sign-in failed (${error}).`)
    if (consent === 'granted') toast.success('Microsoft admin consent granted')
    if (activated === 'activated') toast.success('Microsoft sign-in enabled')
    if (!error && !consent && !activated) return
    const next = new URLSearchParams(params)
    next.delete('sso_error')
    next.delete('sso_consent')
    next.delete('sso')
    setParams(next, { replace: true })
  }, [error, consent, activated, params, setParams])

  return null
}

function HomeRedirect() {
  const { user } = useContext(AuthContext)
  if (!user) return null
  if (isPlatformUser(user)) return <Navigate to="/admin/organizations" replace />
  if (user.orgRole === 'admin' && user.orgId) return <Navigate to={`/admin/organizations/${user.orgId}/overview`} replace />
  if (user.tenantRole === 'admin' && user.tenantId) return <Navigate to="/admin/tenant" replace />
  if (user.tenantId) return <Navigate to="/dashboard" replace />
  return <Navigate to="/account" replace />
}

// A gate's own Outlet is the "nearest Outlet" for pages below it, so it must
// forward the shell's outlet context or useOutletContext() in those pages
// resolves to undefined (blank-page crash — #83).
export function GateOutlet() {
  return <Outlet context={useOutletContext()} />
}

export function TenantMemberOnly() {
  const { user } = useContext(AuthContext)
  return user?.tenantId ? <GateOutlet /> : <Navigate to="/" replace />
}

function LegacyManageRedirect() {
  const { pathname, search, hash } = useLocation()
  return <Navigate to={`${pathname.replace(/^\/manage/, '/admin')}${search}${hash}`} replace />
}

function LazyModule({ id, name, children }: { id: string; name: string; children: ReactNode }) {
  return (
    <ModuleBoundary key={id} moduleId={id} moduleName={name}>
      <Suspense fallback={<div className="flex h-full items-center justify-center text-sm text-muted-foreground" role="status">Opening {name}…</div>}>
        <ModuleReady moduleId={id}>{children}</ModuleReady>
      </Suspense>
    </ModuleBoundary>
  )
}
